import { registerDecorator, ValidationArguments } from 'class-validator';

import { isValidCardNumberLuhn } from '../helpers/detectVisa2PayCardType';
import { translateErrorKey } from '../i18n/request-translate';

import { TI18nValidationOptions } from './class.validators';

function getUtcDateParts(value: string): [number, number, number] | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);

  if (
    !Number.isInteger(year) ||
    !Number.isInteger(month) ||
    !Number.isInteger(day)
  ) {
    return null;
  }

  return [year, month, day];
}

function createUtcDate(year: number, month: number, day: number): Date {
  return new Date(Date.UTC(year, month - 1, day));
}

function getTodayUtc(): Date {
  const now = new Date();
  return createUtcDate(
    now.getUTCFullYear(),
    now.getUTCMonth() + 1,
    now.getUTCDate(),
  );
}

function isRealIsoDate(value: string): boolean {
  const parts = getUtcDateParts(value);
  if (!parts) return false;

  const [year, month, day] = parts;
  const date = createUtcDate(year, month, day);

  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
}

function isDateOfBirthInAllowedRange(value: string): boolean {
  const parts = getUtcDateParts(value);
  if (!parts) return false;

  const [year, month, day] = parts;
  const date = createUtcDate(year, month, day);
  const today = getTodayUtc();

  if (date.getTime() > today.getTime()) {
    return false;
  }

  const oldestAllowedDate = createUtcDate(
    today.getUTCFullYear() - 120,
    today.getUTCMonth() + 1,
    today.getUTCDate(),
  );

  return date.getTime() >= oldestAllowedDate.getTime();
}

export function MinLengthIfNotEmpty(
  minLength: number,
  validationOptions?: TI18nValidationOptions,
) {
  const key = validationOptions?.messageKey || 'validation.minLengthIfNotEmpty';
  const message = () => translateErrorKey(key);
  if (validationOptions?.messageKey) message.key = key;
  return function (object: Record<string, unknown>, propertyName: string) {
    registerDecorator({
      name: 'MinLengthIfNotEmpty',
      target: object.constructor,
      propertyName: propertyName,
      constraints: [minLength],
      options: {
        ...validationOptions,
        groups: validationOptions?.groups || ['error'],
      },
      validator: {
        validate(value: unknown, args: ValidationArguments) {
          const [minLength] = args.constraints;
          if (!value || typeof value !== 'string' || !value.length) return true;
          return value.length >= minLength;
        },
        defaultMessage: message,
      },
    });
  };
}

export function SumArrayItemsPropertyEquals(
  property: string,
  sum: number,
  validationOptions?: TI18nValidationOptions,
) {
  const key =
    validationOptions?.messageKey || 'validation.sumArrayItemsPropertyEquals';
  const message = () => translateErrorKey(key);
  if (validationOptions?.messageKey) message.key = key;
  return function (object: Record<string, unknown>, propertyName: string) {
    registerDecorator({
      name: 'SumArrayItemsPropertyEquals',
      target: object.constructor,
      propertyName: propertyName,
      constraints: [property, sum],
      options: {
        ...validationOptions,
        groups: validationOptions?.groups || ['error'],
      },
      validator: {
        validate(value: unknown[], args: ValidationArguments) {
          const [property, sumMustBe] = args.constraints;
          if (!value || !Array.isArray(value) || !value.length) return true;
          const sum = value.reduce((acc: number, v) => {
            const item = v as Record<string, unknown>;
            const propValue = item[property as string] as number;
            acc += propValue || 0;
            return acc;
          }, 0);
          return sum === sumMustBe;
        },
        defaultMessage: message,
      },
    });
  };
}

export function IsValidDateOfBirth(validationOptions?: TI18nValidationOptions) {
  return function (object: object, propertyName: string) {
    registerDecorator({
      name: 'IsValidDateOfBirth',
      target: object.constructor,
      propertyName,
      options: {
        ...validationOptions,
        groups: validationOptions?.groups || ['error'],
      },
      validator: {
        validate(value: unknown) {
          if (value == null || value === '') return true;
          if (typeof value !== 'string') return false;
          if (!isRealIsoDate(value)) return false;
          return isDateOfBirthInAllowedRange(value);
        },
        defaultMessage(args: ValidationArguments) {
          return `${args.property} must be a real date in YYYY-MM-DD format, not in the future, and not older than 120 years`;
        },
      },
    });
  };
}

export function IsLuhnCardNumber(validationOptions?: TI18nValidationOptions) {
  const key = validationOptions?.messageKey || 'validation.isLuhnCardNumber';
  const message = () => translateErrorKey(key);
  if (validationOptions?.messageKey) message.key = key;

  return function (object: object, propertyName: string) {
    registerDecorator({
      name: 'IsLuhnCardNumber',
      target: object.constructor,
      propertyName,
      options: {
        ...validationOptions,
        groups: validationOptions?.groups || ['error'],
      },
      validator: {
        validate(value: unknown) {
          if (typeof value !== 'string') {
            return false;
          }

          return isValidCardNumberLuhn(value);
        },
        defaultMessage: message,
      },
    });
  };
}
